<div class="scheduler-container" [class.loading]="isGlobalLoading || isSubmitting">
  <button mat-icon-button (click)="gotoDashboard()" aria-label="Go back" class="back-button">
    <mat-icon>arrow_back</mat-icon>
  </button>
  <mat-card class="schedule-card">
    <mat-card-header class="card-header">
      <mat-card-title>{{ getActionText() }}</mat-card-title>
    </mat-card-header>

    <mat-card-content>
      <ng-container *ngIf="appointmentData && isConfirmationView">
        <div class="reschedule-message" *ngIf="currentAppointmentDetails">
          <mat-icon class="info-icon">info</mat-icon>
          <p>Your appointment has been successfully rescheduled to:</p>
        </div>
        <mat-card class="appointment-card" [ngClass]="getStatusColor('Scheduled')">
          <mat-card-header>
            <mat-icon class="status-icon" [color]="getStatusColor('Scheduled')">
              {{ getStatusIcon('Scheduled') }}
            </mat-icon>
            <mat-card-title>{{ formatAppointmentDate(appointmentData.appointmentDateTime) }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <p>For: {{appointmentData.summary}}</p>
            <p>Duration: {{ appointmentData.lengthInMinutes }} mins</p>
          </mat-card-content>
        </mat-card>
      </ng-container>
      <ng-container *ngIf="currentAppointmentDetails && !isConfirmationView">
        <mat-card class="appointment-card" [ngClass]="getStatusColor(currentAppointmentDetails.appointmentStatus)">
          <mat-card-header>
            <mat-icon class="status-icon" [color]="getStatusColor(currentAppointmentDetails.appointmentStatus)">
              {{ getStatusIcon(currentAppointmentDetails.appointmentStatus) }}
            </mat-icon>
            <mat-card-title>{{ formatAppointmentDate(currentAppointmentDetails.appointmentDateTime) }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <p>Duration: {{ currentAppointmentDetails.lengthInMinutes }} mins</p>
            <p>Status: {{ getStatusDisplay(currentAppointmentDetails.appointmentStatus) }}</p>
          </mat-card-content>
        </mat-card>
      </ng-container>
      <div *ngIf="isTreatmentPlanLoading" class="loading-container">
        <mat-spinner diameter="40" color="primary"></mat-spinner>
        <p>Loading treatment plans...</p>
      </div>
      
      <div *ngIf="hasTreatmentPlans && activeTreatmentPlan && !isTreatmentPlanLoading && !isConfirmationView" class="treatment-plans-section">
        <h4>You have an Active Treatment Plan</h4>
      </div>
      <form [formGroup]="appointmentForm" *ngIf="!isConfirmationView">
        <ng-container *ngIf="showTreatmentInput && !appointmentId && !appointmentData">
          <div class="chat-input-container">
            <mat-form-field appearance="outline" class="chat-input full-width">
              <mat-label>Describe your dental need</mat-label>
              <textarea matInput
                        formControlName="appointmentDescription"
                        placeholder="E.g., Teeth cleaning, cavity filling, etc."
                        cdkTextareaAutosize
                        #autosize="cdkTextareaAutosize"
                        cdkAutosizeMinRows="3"
                        cdkAutosizeMaxRows="5"
                        maxlength="300"
                        [matAutocomplete]="auto"
                        (keydown.enter)="onEnter($event)"
                        (focus)="showSuggestions = true"
                        (blur)="hideSuggestions()"
                        class="full-width"></textarea>
              <mat-autocomplete #auto="matAutocomplete" (optionSelected)="onEnter(null)">
                <mat-option *ngFor="let treatment of filteredTreatments | async" [value]="treatment" class="suggestion-option">
                  <mat-icon class="suggestion-icon">trending_up</mat-icon>
                  {{treatment}}
                </mat-option>
              </mat-autocomplete>
              <button mat-icon-button
                      matSuffix
                      (click)="onAppointmentDescriptionSubmit()"
                      [disabled]="!appointmentForm.get('appointmentDescription')?.value || isLoading"
                      class="send-button">
                <mat-icon *ngIf="!isLoading">send</mat-icon>
              </button>
            </mat-form-field>
          </div>
        </ng-container>

        <ng-container *ngIf="isLoading && (isAppointmentTypesLoading || isOperatoriesLoading || isSlotsLoading)">
          <mat-hint>{{loadingStatus}}</mat-hint>
          <mat-progress-spinner mode="indeterminate" diameter="30" color="primary" class="loading-spinner-small"></mat-progress-spinner>
        </ng-container>

        <div class="form-row" *ngIf="isDataReady && isDatepickerVisible">
          <div class="date-picker-field">
            <!-- <mat-label>Date</mat-label> -->
            <mat-calendar
              #calendar
              [headerComponent]="customCalendarHeader"
              (selectedChange)="dateChanged($event)"
              [minDate]="minDate"
              [maxDate]="maxDate"
              [selected]="appointmentForm.get('date')?.value"
              [dateClass]="dateClass">
            </mat-calendar>
            <div *ngIf="isMonthLoading()" class="calendar-loading-overlay">
              <mat-progress-spinner mode="indeterminate" diameter="30" color="primary"></mat-progress-spinner>
            </div>
          </div>
        </div>

        <div class="form-row" *ngIf="isDataReady && !isLoading">
          <div class="slots-container" *ngIf="selectedDate">
            <h3 *ngIf="getObjectKeys(displayedSlots).length > 0">Select a time slot for <b>{{ userFriendlyDate }}:</b></h3>
            <p *ngIf="getObjectKeys(displayedSlots).length === 0">No available time slots for <b>{{ userFriendlyDate }}:</b></p>

            <div class="slots-content" *ngIf="getObjectKeys(displayedSlots).length > 0">
              <div *ngFor="let operatory of getObjectKeys(displayedSlots)">
                <h4>Room {{ operatory }}</h4>
                <mat-chip-listbox>
                  <mat-chip-option *ngFor="let slot of displayedSlots[operatory]"
                                   [selected]="slot === selectedSlot"
                                   (click)="selectSlot(slot)">
                    <span class="time-slot-text">
                      {{ slot.start | date:'h:mm a' }}
                    </span>
                  </mat-chip-option>
                </mat-chip-listbox>

                <!-- Schedule button appears inside the room where time was selected -->
                <div class="room-schedule-button" *ngIf="selectedSlot && selectedSlot.operatory === operatory && !isConfirmationView">
                  <button mat-raised-button class="schedule-button" (click)="getButtonAction()" [disabled]="isSubmitting">
                    {{ getActionText() }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="slots-container" *ngIf="availableSlots.length == 0">
            <h3>No available time slots for this month</h3>
          </div>
        </div>
      </form>
    </mat-card-content>

    <!-- Confirmation view button (after appointment is booked) -->
    <div class="confirmation-actions" *ngIf="isConfirmationView">
      <button mat-raised-button class="schedule-button" (click)="getButtonAction()" [disabled]="isSubmitting">
        {{ getActionText() }}
      </button>
    </div>

    <div *ngIf="isSubmitting" class="global-loading-overlay">
      <mat-progress-spinner mode="indeterminate" diameter="40" color="primary"></mat-progress-spinner>
    </div>
  </mat-card>
</div>